import { CSSProperties, FC, useMemo, useRef, useState } from 'react'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { Skeleton } from 'src/components/Skeleton'
import styles from './sentimentDistributionChart.module.scss'

// Real geometry from sentimentDistributionChart.module.scss / the layout below:
// the plot is 216px tall (.yAxis height matches chartHeight), y-axis labels are
// 12px, and a non-compact column is 96px wide with a 60px bar.
const SKELETON_BAR_WIDTH = 60
const SKELETON_CHART_HEIGHT = 216
const SKELETON_COLUMNS = 5

export interface PlatformSentimentData {
  negative: number
  neutral: number
  platform: string
  positive: number
}

type SegmentType = 'negative' | 'neutral' | 'positive'

interface TooltipState {
  hoveredSegment: SegmentType
  negative: number
  neutral: number
  platform: string
  positive: number
  x: number
  y: number
}

interface SentimentDistributionChartProps {
  data: PlatformSentimentData[]
  isLoading?: boolean
}

const formatLabel = (value: number): string => {
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace('.0', '')}k`
  return `${value}`
}

const getNiceStep = (maxValue: number, steps: number): number => {
  if (maxValue <= steps) return 1

  const roughStep = maxValue / steps
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalized = roughStep / magnitude

  if (normalized <= 1) return magnitude
  if (normalized <= 2) return 2 * magnitude
  if (normalized <= 5) return 5 * magnitude

  return 10 * magnitude
}

const SEGMENT_META: Record<SegmentType, { color: string; label: string }> = {
  negative: { color: '#FA7138', label: 'Negative Mentions' },
  neutral: { color: '#C7CED1', label: 'Neutral Mentions' },
  positive: { color: '#958BF1', label: 'Positive Mentions' },
}

const Y_AXIS_STEPS = 5

export const SentimentDistributionChart: FC<
  SentimentDistributionChartProps
> = ({ data, isLoading = false }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const layout = useMemo(() => {
    const compact = data.length > 9
    const columnWidth = compact ? 74 : 96
    const columnPadding = compact ? 8 : 18
    const barWidth = compact ? 46 : 60
    const axisWidth = 120
    const chartMinWidth = Math.max(
      960,
      axisWidth + data.length * (columnWidth + columnPadding * 2)
    )

    return {
      barWidth,
      chartMinWidth,
      columnPadding,
      columnWidth,
      compact,
    }
  }, [data.length])

  const maxTotal = data.reduce((max, d) => {
    const total = d.positive + d.negative + d.neutral
    return total > max ? total : max
  }, 0)

  // Before the empty state: `data` is [] until the response lands, so the chart
  // used to claim "No platform data available" while still loading — and that
  // empty state is a 120px box against a 216px plot, so the section visibly
  // jumped taller when the bars arrived.
  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.chartArea}>
          <div className={styles.yAxis}>
            {Array.from({ length: Y_AXIS_STEPS + 1 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton height={12} key={index} width={84} />
            ))}
          </div>
          <div className={styles.chartScroller}>
            <div className={styles.barsContainer}>
              <div className={styles.bars}>
                {Array.from({ length: SKELETON_COLUMNS }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={styles.barColumn} key={index}>
                    <Skeleton
                      height={SKELETON_CHART_HEIGHT}
                      width={SKELETON_BAR_WIDTH}
                    />
                  </div>
                ))}
              </div>
              <div className={styles.baseline} />
              <div className={styles.platformLabelsRow}>
                {Array.from({ length: SKELETON_COLUMNS }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={styles.platformLabelCell} key={index}>
                    <Skeleton height={12} width={56} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.legend}>
          {(['positive', 'negative', 'neutral'] as SegmentType[]).map((s) => (
            <div className={styles.legendItem} key={s}>
              <Skeleton circle width={8} />
              <Skeleton height={12} width={52} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (data.length === 0 || maxTotal === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.emptyState}>No platform data available</div>
      </div>
    )
  }

  const paddedMax = Math.ceil(maxTotal * 1.1)
  const step = getNiceStep(paddedMax, Y_AXIS_STEPS)
  const yMax = Math.max(step, Math.ceil(paddedMax / step) * step)

  const yLabels: number[] = []
  for (let i = Y_AXIS_STEPS; i >= 0; i--) {
    yLabels.push(Math.round((yMax / Y_AXIS_STEPS) * i))
  }

  const chartHeight = 216
  const styleVars = {
    '--bar-width': `${layout.barWidth}px`,
    '--chart-min-width': `${layout.chartMinWidth}px`,
    '--column-padding': `${layout.columnPadding}px`,
    '--column-width': `${layout.columnWidth}px`,
  } as CSSProperties

  const handleEnter = (
    e: React.MouseEvent,
    d: PlatformSentimentData,
    segment: SegmentType
  ) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setTooltip({
      hoveredSegment: segment,
      negative: d.negative,
      neutral: d.neutral,
      platform: d.platform,
      positive: d.positive,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const handleMove = (e: React.MouseEvent, segment: SegmentType) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setTooltip((prev) =>
      prev
        ? {
            ...prev,
            hoveredSegment: segment,
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          }
        : null
    )
  }

  const handleLeave = () => setTooltip(null)

  return (
    <div className={styles.wrapper} ref={containerRef} style={styleVars}>
      {/* Tooltip */}
      {tooltip && (
        <div
          className={styles.tooltipBox}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={styles.tooltipHeader}>
            <span className={styles.tooltipIcon}>
              {SOCIAL_ICONS[tooltip.platform.toLowerCase()] ?? null}
            </span>
            <span className={styles.tooltipPlatform}>
              {platformDisplayLabel(tooltip.platform)}
            </span>
          </div>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Total Mentions:</span>
            <span className={styles.tooltipValue}>
              {(
                tooltip.positive +
                tooltip.negative +
                tooltip.neutral
              ).toLocaleString()}
            </span>
          </div>
          <div className={styles.tooltipRow}>
            <span
              className={styles.tooltipDot}
              style={{
                background: SEGMENT_META[tooltip.hoveredSegment].color,
              }}
            />
            <span className={styles.tooltipLabel}>
              {SEGMENT_META[tooltip.hoveredSegment].label}:
            </span>
            <span className={styles.tooltipValue}>
              {tooltip[tooltip.hoveredSegment].toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Chart area */}
      <div className={styles.chartArea}>
        {/* Y-axis labels */}
        <div className={styles.yAxis}>
          {yLabels.map((val) => (
            <span className={styles.yLabel} key={val}>
              {val === 0 ? '0 Mention' : `${formatLabel(val)} Mentions`}
            </span>
          ))}
        </div>

        <div className={styles.chartScroller}>
          {/* Bars + baseline + platform labels */}
          <div className={styles.barsContainer}>
            <div className={styles.bars}>
              {data.map((d) => {
                const posH = (d.positive / yMax) * chartHeight
                const negH = (d.negative / yMax) * chartHeight
                const neuH = (d.neutral / yMax) * chartHeight

                return (
                  <div className={styles.barColumn} key={d.platform}>
                    <div
                      className={styles.barInner}
                      style={{ height: chartHeight }}
                    >
                      <div className={styles.stackedBar}>
                        {posH > 0 && (
                          <div
                            className={styles.segment}
                            onMouseEnter={(e) => handleEnter(e, d, 'positive')}
                            onMouseLeave={handleLeave}
                            onMouseMove={(e) => handleMove(e, 'positive')}
                            style={{
                              background: SEGMENT_META.positive.color,
                              height: posH,
                            }}
                          >
                            {posH > 18 && (
                              <span className={styles.segmentLabel}>
                                {formatLabel(d.positive)}
                              </span>
                            )}
                          </div>
                        )}

                        {negH > 0 && (
                          <div
                            className={styles.segment}
                            onMouseEnter={(e) => handleEnter(e, d, 'negative')}
                            onMouseLeave={handleLeave}
                            onMouseMove={(e) => handleMove(e, 'negative')}
                            style={{
                              background: SEGMENT_META.negative.color,
                              height: negH,
                            }}
                          >
                            {negH > 18 && (
                              <span className={styles.segmentLabel}>
                                {formatLabel(d.negative)}
                              </span>
                            )}
                          </div>
                        )}

                        {neuH > 0 && (
                          <div
                            className={`${styles.segment} ${styles.segmentTop}`}
                            onMouseEnter={(e) => handleEnter(e, d, 'neutral')}
                            onMouseLeave={handleLeave}
                            onMouseMove={(e) => handleMove(e, 'neutral')}
                            style={{
                              background: SEGMENT_META.neutral.color,
                              height: neuH,
                            }}
                          >
                            {neuH > 18 && (
                              <span className={styles.segmentLabel}>
                                {formatLabel(d.neutral)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className={styles.baseline} />

            <div className={styles.platformLabelsRow}>
              {data.map((d) => (
                <div className={styles.platformLabelCell} key={d.platform}>
                  <span
                    className={styles.platformLabel}
                    title={
                      layout.compact
                        ? platformDisplayLabel(d.platform)
                        : undefined
                    }
                  >
                    {platformDisplayLabel(d.platform)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        {(['positive', 'negative', 'neutral'] as SegmentType[]).map((s) => (
          <div className={styles.legendItem} key={s}>
            <span
              className={styles.legendDot}
              style={{ background: SEGMENT_META[s].color }}
            />
            <span className={styles.legendText}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
